/**
 * DOM-based search utilities
 * Used for sections where DOM search is more reliable than data-based search
 * (e.g., OpenAI with complex markdown rendering, Tags with dynamic content, Latest Deployed, Details)
 */

import { SearchResult } from './SearchContext';
import { getSearchableTextNodes } from './sharedDomHelpers';

interface DOMSearchOptions {
  section: SearchResult['section'];
  field: string;
  searchTerm: string;
  sectionSelector: string;
  globalMatchIndex: { value: number };
}

/**
 * Search for a term within a DOM element and return search results
 * This function searches the actual rendered DOM, ensuring that:
 * - Hidden elements are automatically excluded
 * - Complex rendering (markdown, transforms) is handled correctly
 */
export const searchInDOM = (
  options: DOMSearchOptions,
  scrollContainerRef?:
    | React.RefObject<HTMLElement>
    | React.MutableRefObject<HTMLElement | undefined>,
): SearchResult[] => {
  const { section, field, searchTerm, sectionSelector, globalMatchIndex } = options;
  const results: SearchResult[] = [];

  const scrollContainer = scrollContainerRef?.current;
  if (!scrollContainer) {
    return results;
  }

  // Find the section element
  const sectionElement = scrollContainer.querySelector(sectionSelector);
  if (!sectionElement) {
    return results;
  }

  // Get all text nodes in the section using shared logic
  const textNodes = getSearchableTextNodes(sectionElement);
  const lowerSearchTerm = searchTerm.toLowerCase();

  // Track occurrences across all text nodes in this section
  let occurrenceIndex = 0;

  // Search through each text node
  textNodes.forEach((textNode) => {
    const text = textNode.textContent || '';
    const lowerText = text.toLowerCase();

    // Find all occurrences in this text node
    let startIndex = 0;

    while (startIndex < lowerText.length) {
      const index = lowerText.indexOf(lowerSearchTerm, startIndex);
      if (index === -1) break;

      results.push({
        sourceText: text, // Store the full text content for context
        section,
        field,
        matchIndex: globalMatchIndex.value++,
        occurrenceIndex,
      });

      occurrenceIndex++;
      startIndex = index + lowerSearchTerm.length; // Move past this match
    }
  });

  return results;
};

/**
 * Check if a section is ready for DOM search
 */
export const isSectionReadyForDOMSearch = (
  sectionSelector: string,
  scrollContainerRef?:
    | React.RefObject<HTMLElement>
    | React.MutableRefObject<HTMLElement | undefined>,
): boolean => {
  const scrollContainer = scrollContainerRef?.current;
  if (!scrollContainer) return false;

  const sectionElement = scrollContainer.querySelector(sectionSelector);
  if (!sectionElement) return false;

  // Section exists in DOM, we can search it (even if collapsed)
  return true;
};
